import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { format, addDays, startOfDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import { Shift } from '../../types/shift';

interface WeeklyViewProps {
  shifts: Shift[];
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  onCellClick: (date: Date, hour: number) => void;
}

const HOURS = Array.from({ length: 24 }, (_, i) => (i + 6) % 24); // 6:00 - 翌5:00 (24時間)
const CELL_WIDTH = 50; // 幅を狭くして全体を表示
const ROW_HEIGHT = 40;

export default function WeeklyView({
  shifts,
  selectedDate,
  onDateChange,
  onCellClick,
}: WeeklyViewProps) {
  // 週の開始日（本日を起点に7日間）
  const weekStart = startOfDay(selectedDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // 指定日時の勤務人数を計算 (6時未満は翌日として扱う)
  const getStaffCountAt = (date: Date, hour: number): number => {
    const targetDayStart = startOfDay(date);

    return shifts.filter((shift) => {
      const shiftDayStart = startOfDay(shift.shiftDate);
      if (shiftDayStart.getTime() !== targetDayStart.getTime()) {
        return false;
      }

      const startHour = parseInt(shift.startTime.split(':')[0]);
      const endHour = parseInt(shift.endTime.split(':')[0]);

      // 開始・終了時刻を6時基準の時刻に変換
      const adjustedStart = startHour < 6 ? startHour + 24 : startHour;
      const adjustedEnd = endHour < 6 ? endHour + 24 : endHour;
      const adjustedHour = hour < 6 ? hour + 24 : hour;
      return adjustedHour >= adjustedStart && adjustedHour < adjustedEnd && shift.status === 'approved';
    }).length;
  };

  // 人数に応じた色を取得
  const getCountColor = (count: number): string => {
    if (count === 0) return '#FFCDD2'; // 赤（人手不足）
    if (count === 1) return '#FFF9C4'; // 黄色（ギリギリ）
    if (count === 2) return '#C8E6C9'; // 緑（適正）
    if (count >= 3) return '#BBDEFB'; // 青（余裕）
    return '#E0E0E0';
  };

  // 人数に応じたテキスト色
  const getCountTextColor = (count: number): string => {
    if (count === 0) return '#D32F2F';
    if (count === 1) return '#F57C00';
    return '#424242';
  };

  return (
    <Box>
      {/* 週選択ヘッダー */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => onDateChange(addDays(selectedDate, -7))}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h6">
            {format(weekStart, 'yyyy年M月d日', { locale: ja })} 〜{' '}
            {format(addDays(weekStart, 6), 'M月d日', { locale: ja })}
          </Typography>
          <IconButton onClick={() => onDateChange(addDays(selectedDate, 7))}>
            <ChevronRightIcon />
          </IconButton>
          <Button
            size="small"
            onClick={() => onDateChange(new Date())}
            sx={{ ml: 2 }}
          >
            今日から
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          クリックして詳細表示
        </Typography>
      </Paper>

      {/* 週間グリッド */}
      <Paper sx={{ p: 2, overflowX: 'auto' }}>
        <Box sx={{ minWidth: 100 + weekDays.length * CELL_WIDTH }}>
          {/* 曜日ヘッダー */}
          <Box sx={{ display: 'flex', mb: 1 }}>
            <Box
              sx={{
                width: 100,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
              }}
            >
              時間
            </Box>
            {weekDays.map((day, index) => {
              const isToday = startOfDay(day).getTime() === startOfDay(new Date()).getTime();
              const dayOfWeek = format(day, 'E', { locale: ja });
              const isSunday = dayOfWeek === '日';
              const isSaturday = dayOfWeek === '土';

              return (
                <Box
                  key={index}
                  sx={{
                    width: CELL_WIDTH,
                    textAlign: 'center',
                    borderLeft: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: isSunday ? 'error.main' : isSaturday ? 'primary.main' : 'text.primary',
                    }}
                  >
                    {dayOfWeek}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isToday ? 'primary.main' : 'text.secondary',
                      fontWeight: isToday ? 'bold' : 'normal',
                    }}
                  >
                    {format(day, 'M/d')}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* 時間行 */}
          {HOURS.map((hour) => (
            <Box
              key={hour}
              sx={{
                display: 'flex',
                borderBottom: '1px solid',
                borderColor: 'divider',
                height: ROW_HEIGHT,
              }}
            >
              {/* 時間ラベル */}
              <Box
                sx={{
                  width: 100,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  bgcolor: 'grey.50',
                }}
              >
                {hour}:00
              </Box>

              {/* 各曜日のセル */}
              {weekDays.map((day, dayIndex) => {
                const count = getStaffCountAt(day, hour);
                const bgColor = getCountColor(count);
                const textColor = getCountTextColor(count);

                return (
                  <Box
                    key={dayIndex}
                    onClick={() => onCellClick(day, hour)}
                    sx={{
                      width: CELL_WIDTH,
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: bgColor,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        opacity: 0.8,
                        boxShadow: 1,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        color: textColor,
                      }}
                    >
                      {count}人
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* 凡例 */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#FFCDD2', borderRadius: 0.5 }} />
          <Typography variant="caption">0人（人手不足）</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#FFF9C4', borderRadius: 0.5 }} />
          <Typography variant="caption">1人（最低限）</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#C8E6C9', borderRadius: 0.5 }} />
          <Typography variant="caption">2人（適正）</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 16, height: 16, bgcolor: '#BBDEFB', borderRadius: 0.5 }} />
          <Typography variant="caption">3人以上（余裕）</Typography>
        </Box>
      </Box>
    </Box>
  );
}
